import { focusManager, type QueryClient } from '@tanstack/react-query';
import { isSessionVerified } from '@/features/auth/session-boundary';
import { ENTRY_WORKSPACE_SAVE_MUTATION_KEY } from '@/features/shiftbook/hooks/query-keys';
import type { EntryWorkspaceSaveInput } from '@/features/shiftbook/hooks/use-entry-workspace-save';

export const WORKSPACE_REPLAY_FAILURE_EVENT = 'blms:workspace-replay-failed';

export type WorkspaceReplayFailureDetail = {
  displayHeading: string | null;
};

function hasPausedWorkspaceSave(queryClient: QueryClient): boolean {
  return queryClient
    .getMutationCache()
    .getAll()
    .some((mutation) => {
      const candidate = mutation as {
        options: { mutationKey?: unknown };
        state: { isPaused: boolean };
      };

      const key = candidate.options.mutationKey;
      return (
        Array.isArray(key) &&
        key[0] === ENTRY_WORKSPACE_SAVE_MUTATION_KEY[0] &&
        candidate.state.isPaused
      );
    });
}

function reportWorkspaceReplayFailure(queryClient: QueryClient): void {
  if (typeof window === 'undefined') {
    return;
  }

  const failedMutation = queryClient
    .getMutationCache()
    .getAll()
    .filter((mutation) => {
      const candidate = mutation as {
        options: { mutationKey?: unknown };
        state: { status: string; isPaused?: boolean; failureCount?: number };
      };

      const key = candidate.options.mutationKey;
      return (
        Array.isArray(key) &&
        key[0] === ENTRY_WORKSPACE_SAVE_MUTATION_KEY[0] &&
        (candidate.state.status === 'error' ||
          (Number(candidate.state.failureCount ?? 0) > 0 && !candidate.state.isPaused))
      );
    })
    .sort((left, right) => {
      const leftSubmittedAt = (left as { state: { submittedAt?: number } }).state.submittedAt ?? 0;
      const rightSubmittedAt =
        (right as { state: { submittedAt?: number } }).state.submittedAt ?? 0;
      return rightSubmittedAt - leftSubmittedAt;
    })[0];

  if (!failedMutation) {
    return;
  }

  const variables = (failedMutation as { state: { variables?: unknown } }).state.variables as
    | EntryWorkspaceSaveInput
    | undefined;
  window.dispatchEvent(
    new CustomEvent<WorkspaceReplayFailureDetail>(WORKSPACE_REPLAY_FAILURE_EVENT, {
      detail: {
        displayHeading: variables?.displayHeading ?? null,
      },
    }),
  );
}

export async function resumePausedMutationsWithReporting(queryClient: QueryClient) {
  if (!isSessionVerified()) return;
  const hadPausedWorkspaceSave = hasPausedWorkspaceSave(queryClient);
  try {
    await queryClient.resumePausedMutations();
  } catch {
    // Fall through so post-resume mutation state can determine whether a
    // workspace replay actually failed.
  }

  if (hadPausedWorkspaceSave) {
    await new Promise((resolve) => window.setTimeout(resolve, 0));
    reportWorkspaceReplayFailure(queryClient);
  }

  // AuthProvider just verified this session; replay refreshes product data.
  await queryClient.invalidateQueries({
    predicate: (query) => query.queryKey[0] !== 'auth-session',
  });
}

export function setupFocusManager() {
  if (typeof window === 'undefined') return;

  focusManager.setEventListener((handleFocus) => {
    const onVisibility = () => handleFocus(!document.hidden);
    const onFocus = () => handleFocus(true);

    document.addEventListener('visibilitychange', onVisibility, false);
    window.addEventListener('focus', onFocus, false);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
    };
  });
}

export function setupOfflineMutationReplay(queryClient: QueryClient) {
  if (typeof window === 'undefined') return () => {};

  const replay = async () => {
    await resumePausedMutationsWithReporting(queryClient);
  };

  const onOnline = () => {
    void replay();
  };

  window.addEventListener('online', onOnline);

  return () => {
    window.removeEventListener('online', onOnline);
  };
}
